import React, { useEffect, useState } from "react";
import {
    Grid,
    Typography,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDocumentTitle } from "../../Hooks/useDocumentTitle";
import { useApplicationContext } from "../../ApplicationContext";
import { GetHostSystemData, AddFileTypeData, GetFileTypeData } from "../../apiClient/Api";
import IconPlaceholder from "./IconPlaceholder";

/**
 * Displays and manages the admin overview for available file types.
 */
export const AdminFileTypes = () => {
    useDocumentTitle("base.pages.fileTypes");
    const { t } = useTranslation();

    const [rows, setRows] = useState<GetFileTypeData[]>([]);
    const [hostSystems, setHostSystems] = useState<GetHostSystemData[]>([]);
    const [isData, setIsData] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newAppName, setNewAppName] = useState("");
    const [newAppHostSystem, setNewAppHostSystem] = useState("");
    const [deleteDialogTarget, setDeleteDialogTarget] = useState<GetFileTypeData | null>(null);
    const [deleteEntryValue, setDeleteEntryValue] = useState("");
    const context = useApplicationContext();
    const controller = context.api.fileType;
    const history = useNavigate();

    /**
     * Opens the dialog for adding a new application entry.
     */
    const handleOpenAddDialog = () => setIsAddDialogOpen(true);

    /**
     * Closes the dialog for adding a new application entry.
     */
    const handleCloseAddDialog = () => {
        setIsAddDialogOpen(false);
        setNewAppName("");
        setNewAppHostSystem("");
    };

    /**
     * Sends a request to create a new application/file type entry.
     */
    const handleAddApp = async () => {
        const newApp: AddFileTypeData = {
            name: newAppName,
            description: "",
            filterTags: [],
            variants: [],
            packageId: "",
            platformConfigurations: newAppHostSystem
                ? [
                    {
                        hostSystemId: newAppHostSystem,
                        matchingFiles: [],
                    },
                ]
                : [],
        };
        try {
            const response = await controller.fileTypeAddFileType(newApp);
            if (response.data) {
                setRows(current => [...current, response.data]);
                handleCloseAddDialog();
            }
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Opens the delete dialog for the provided file type row.
     * @param fileType File type that should be deleted.
     */
    const handleDeleteClick = (fileType: GetFileTypeData) => {
        setDeleteDialogTarget(fileType);
        setDeleteEntryValue("");
    };

    /**
     * Closes the delete dialog and clears the form state.
     */
    const handleDeleteDialogClose = () => {
        setDeleteDialogTarget(null);
        setDeleteEntryValue("");
    };

    /**
     * Confirms the deletion of the selected file type.
     */
    const handleConfirmDelete = async () => {
        if (!deleteDialogTarget || deleteEntryValue !== deleteDialogTarget.name) {
            return;
        }
        try {
            await controller.fileTypeDeleteFileType(deleteDialogTarget.id);
            setRows(current => current.filter(r => r.id !== deleteDialogTarget.id));
            handleDeleteDialogClose();
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Loads host systems and file type data from the API.
     */
    const fetchData = async () => {
        try {
            const hostSystemsResponse = await controller.fileTypeGetHostSystems();
            setHostSystems(hostSystemsResponse.data);
            const fileTypesResponse = await controller.fileTypeGetFileTypes();
            setRows(fileTypesResponse.data);
        } catch (e) {
            context.apiError(e);
        }
    };

    useEffect(() => {
        if (!isData) {
            setIsData(true);
            fetchData();
        }
    }, [isData]);

    /**
     * Creates the public logo url for the provided image id.
     * @param logoId Identifier of the logo image.
     */
    const getLogoUrl = (logoId: string | null) => {
        // @ts-ignore
        const serverUrl = ServerConfig.SERVER_URL;
        return logoId ? `${serverUrl}/FileType/Image/${logoId}` : "";
    };

    const isDeleteDisabled = !deleteDialogTarget || deleteEntryValue !== deleteDialogTarget.name;

    return (
        <>
            <Dialog
                open={deleteDialogTarget !== null}
                onClose={handleDeleteDialogClose}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">{t("adminFileTypes.deleteDialog.title")}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {t("adminFileTypes.deleteDialog.description")}
                    </DialogContentText>
                    {deleteDialogTarget && (
                        <>
                            <DialogContentText fontWeight={"bold"}>
                                {t("adminFileTypes.deleteDialog.instruction", { name: deleteDialogTarget.name })}
                            </DialogContentText>
                            <TextField
                                autoFocus
                                margin="dense"
                                label={t("adminFileTypes.deleteDialog.textFieldLabel")}
                                value={deleteEntryValue}
                                onChange={e => setDeleteEntryValue(e.target.value)}
                                fullWidth
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} color="primary">
                        {t("adminFileTypes.deleteDialog.cancel")}
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" disabled={isDeleteDisabled}>
                        {t("adminFileTypes.deleteDialog.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isAddDialogOpen} onClose={handleCloseAddDialog}>
                <DialogTitle>{t("adminFileTypes.addDialog.title")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t("adminFileTypes.addDialog.nameLabel")}
                        fullWidth
                        value={newAppName}
                        onChange={e => setNewAppName(e.target.value)}
                    />
                    <TextField
                        select
                        margin="dense"
                        label={t("adminFileTypes.addDialog.hostSystemLabel")}
                        fullWidth
                        value={newAppHostSystem}
                        onChange={e => setNewAppHostSystem(e.target.value)}
                    >
                        <MenuItem value="">{t("adminFileTypes.addDialog.hostSystemPlaceholder")}</MenuItem>
                        {hostSystems.map((hostSystem, index) => (
                            <MenuItem key={hostSystem.id ?? hostSystem.name ?? `host-${index}`} value={hostSystem.id ?? ""}>
                                {hostSystem.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddDialog} color="primary">
                        {t("adminFileTypes.addDialog.cancel")}
                    </Button>
                    <Button onClick={handleAddApp} color="primary" disabled={!newAppName.trim()}>
                        {t("adminFileTypes.addDialog.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Button color="primary" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
                {t("adminFileTypes.addButton")}
            </Button>

            <Grid container spacing={2}>
                {rows.map((row) => (
                    <Grid item key={row.id} style={{ width: 250 }} >
                        <Card  onClick={() => history(`/admin/filetypes/${row.id}`)}>
                            <CardContent sx={{ display: 'flex', justifyContent: 'center' , flexDirection: 'column', alignItems: 'center'}}>
                            <Box style={{ marginBottom: 8 }}>
                                {row.iconId ? (
                                    <img src={getLogoUrl(row.iconId)} alt={row.name} style={{ width: 100 }} />
                                ) : (
                                    
                                        <IconPlaceholder />
                                )}
                                </Box>
                                <Typography variant="h6">{row.name}</Typography>
                                <Typography variant="caption">{t("adminFileTypes.cards.hostSystem")}</Typography>
                                <Typography variant="body2">{hostSystems.find(data => data.id === row.platformConfigurations?.[0]?.hostSystemId)?.name}</Typography>
                                <Typography variant="caption">{t("adminFileTypes.cards.packageId")}</Typography>
                                <Typography variant="body2">{row.packageId ?? "-"} </Typography>
                            </CardContent>
                            <CardActions>
                                <IconButton onClick={() => history(`/admin/filetypes/${row.id}`)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleDeleteClick(row);
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </>
    );
};
